import { Building2, Download, LogIn, Sparkles, UserCheck, Users, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  ErrorState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { formatNumber } from '@/lib/utils';

import { downloadReportCsv, useReport, type ReportFilter } from './admin-actions-api';
import {
  ACTION_KEYS,
  GRANULARITIES,
  type Granularity,
  type Report,
  type ReportKind,
} from './admin-schemas';
import { BillingReport } from './billing-report';
import { BarList, Kpi, TimeBars, percent } from './charts';
import { formatDay } from './labels';

type BizRow = Report['businesses'][number];
type SummaryMetric =
  | keyof Report['totals']['actions']
  | 'newBusinesses'
  | 'activeBusinesses'
  | 'suspended'
  | 'pendingDeletion'
  | 'newUsers'
  | 'uniqueLogins'
  | 'passwordLogins'
  | 'googleLogins';
type AiRow = Report['aiBudget'][number];

const israelDay = (d: Date) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem' }).format(d);
const shekels = (minor: number) => `₪${(minor / 100).toFixed(2)}`;

/**
 * דוחות: טווח תאריכים ורזולוציה, לכל הפלטפורמה או לעסק אחד. ספירות בלבד —
 * בלי פרטי לקוחות. כל טבלה יורדת כ-CSV מהשרת (עם BOM, נפתח בעברית ב-Excel).
 */
export function ReportsSection() {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const today = israelDay(new Date());
  const [filter, setFilter] = useState<ReportFilter>({
    from: israelDay(new Date(Date.now() - 29 * 86_400_000)),
    to: today,
    granularity: 'day',
  });
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<ReportKind | null>(null);
  const q = useReport(filter);
  const d = q.data;

  const download = async (kind: ReportKind) => {
    setDownloading(kind);
    try {
      await downloadReportCsv(filter, kind);
    } catch (err) {
      toast.error(t('reports.exportFailed'), err instanceof ApiError ? err.message : undefined);
    } finally {
      setDownloading(null);
    }
  };

  const csv = (kind: ReportKind) => (
    <Button
      size="sm"
      variant="secondary"
      loading={downloading === kind}
      onClick={() => void download(kind)}
    >
      <Download aria-hidden />
      {t('reports.csv')}
    </Button>
  );

  const bucketLabel = (b: string) =>
    filter.granularity === 'month' ? b.slice(0, 7) : formatDay(b);

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3">
          <Field label={t('reports.from')} htmlFor="rep-from" className="w-40">
            <Input
              id="rep-from"
              type="date"
              value={filter.from}
              max={filter.to}
              onChange={(e) => e.target.value && setFilter((f) => ({ ...f, from: e.target.value }))}
            />
          </Field>
          <Field label={t('reports.to')} htmlFor="rep-to" className="w-40">
            <Input
              id="rep-to"
              type="date"
              value={filter.to}
              min={filter.from}
              max={today}
              onChange={(e) => e.target.value && setFilter((f) => ({ ...f, to: e.target.value }))}
            />
          </Field>
          <Field label={t('reports.granularity')} htmlFor="rep-gran" className="w-36">
            <Select
              value={filter.granularity}
              onValueChange={(v) => setFilter((f) => ({ ...f, granularity: v as Granularity }))}
            >
              <SelectTrigger id="rep-gran">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GRANULARITIES.map((g) => (
                  <SelectItem key={g} value={g}>
                    {t(`reports.granularities.${g}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {filter.businessId ? (
            <Badge tone="accent" dot={false} className="h-9">
              {businessName}
              <button
                type="button"
                aria-label={t('reports.clearBusiness')}
                onClick={() => {
                  setFilter(({ businessId: _b, ...f }) => f);
                  setBusinessName(null);
                }}
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </Badge>
          ) : null}
        </CardContent>
      </Card>

      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi
              icon={Building2}
              tone="accent"
              label={t('reports.kpi.businesses')}
              value={d?.totals.businesses.total}
            />
            <Kpi
              icon={Users}
              tone="violet"
              label={t('reports.kpi.users')}
              value={d?.totals.users.total}
            />
            <Kpi
              icon={UserCheck}
              tone="teal"
              label={t('reports.kpi.activeUsers')}
              value={d?.totals.users.active}
            />
            <Kpi
              icon={LogIn}
              tone="orange"
              label={t('reports.kpi.logins')}
              value={d?.totals.logins.count}
            />
          </div>

          {d ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>{t('reports.summary')}</CardTitle>
                  <CardDescription>{t('reports.summaryHint')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    {(
                      [
                        ['newBusinesses', d.totals.businesses.new],
                        ['activeBusinesses', d.totals.businesses.active],
                        ['suspended', d.totals.businesses.suspended],
                        ['pendingDeletion', d.totals.businesses.pendingDeletion],
                        ['newUsers', d.totals.users.new],
                        ['uniqueLogins', d.totals.logins.uniqueUsers],
                        ['passwordLogins', d.totals.logins.password],
                        ['googleLogins', d.totals.logins.google],
                        ...ACTION_KEYS.map((k) => [k, d.totals.actions[k]] as const),
                        ['pageViews', d.totals.actions.pageViews],
                        ['clicks', d.totals.actions.clicks],
                      ] as Array<readonly [SummaryMetric, number]>
                    ).map(([k, v]) => (
                      <Stat
                        key={k}
                        label={t(`reports.metrics.${k}`)}
                        value={formatNumber(Number(v))}
                      />
                    ))}
                    <Stat
                      label={t('reports.metrics.aiCost')}
                      value={shekels(d.totals.aiCostMinor)}
                    />
                  </dl>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex-row items-start justify-between gap-3">
                  <div>
                    <CardTitle>{t('reports.overTime')}</CardTitle>
                    <CardDescription>{t(`reports.granularities.${d.granularity}`)}</CardDescription>
                  </div>
                  {csv('series')}
                </CardHeader>
                <CardContent className="grid gap-6 md:grid-cols-2">
                  {(
                    [
                      ['logins', 'accent'],
                      ['activeUsers', 'teal'],
                      ['newBusinesses', 'violet'],
                      ['quotesCreated', 'accent'],
                      ['tasksCreated', 'teal'],
                      ['aiCostMinor', 'violet'],
                    ] as const
                  ).map(([key, tone]) => (
                    <section key={key}>
                      <h3 className="mb-2 text-xs font-semibold text-fg">
                        {t(`reports.metrics.${key}`)}
                      </h3>
                      <TimeBars
                        tone={tone}
                        label={t(`reports.metrics.${key}`)}
                        points={d.series.map((p) => ({
                          key: p.bucket,
                          value: p[key],
                          title: bucketLabel(p.bucket),
                        }))}
                        describe={(p) =>
                          `${t(`reports.metrics.${key}`)} · ${bucketLabel(p.key)}: ${
                            key === 'aiCostMinor' ? shekels(p.value) : formatNumber(p.value)
                          }`
                        }
                      />
                    </section>
                  ))}
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <CardHeader className="flex-row items-start justify-between gap-3">
                  <div>
                    <CardTitle>{t('reports.byBusiness')}</CardTitle>
                    <CardDescription>{t('reports.byBusinessHint')}</CardDescription>
                  </div>
                  {csv('businesses')}
                </CardHeader>
                <DataTable<BizRow>
                  caption={t('reports.byBusiness')}
                  minWidth="64rem"
                  rows={d.businesses.slice(0, 200)}
                  rowKey={(r) => r.id}
                  columns={[
                    {
                      key: 'name',
                      header: t('businesses.columns.name'),
                      cell: (r) => (
                        <button
                          type="button"
                          className="text-start font-medium text-accent hover:underline"
                          onClick={() => {
                            setFilter((f) => ({ ...f, businessId: r.id }));
                            setBusinessName(r.name);
                          }}
                        >
                          {r.name}
                          {r.status !== 'active' ? (
                            <Badge tone="danger" className="ms-1.5">
                              {t(`status.${r.status}`)}
                            </Badge>
                          ) : null}
                        </button>
                      ),
                    },
                    {
                      key: 'users',
                      header: t('reports.metrics.users'),
                      numeric: true,
                      cell: (r) => formatNumber(r.users),
                    },
                    {
                      key: 'activeUsers',
                      header: t('reports.metrics.activeUsers'),
                      numeric: true,
                      cell: (r) => formatNumber(r.activeUsers),
                    },
                    {
                      key: 'logins',
                      header: t('reports.metrics.logins'),
                      numeric: true,
                      cell: (r) => formatNumber(r.logins),
                    },
                    ...ACTION_KEYS.map((k) => ({
                      key: k,
                      header: t(`reports.metrics.${k}`),
                      numeric: true,
                      cell: (r: BizRow) => formatNumber(r[k]),
                    })),
                    {
                      key: 'ai',
                      header: t('reports.metrics.aiCost'),
                      numeric: true,
                      cell: (r) => shekels(r.aiCostMinor),
                    },
                  ]}
                />
              </Card>

              <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                  <CardHeader className="flex-row items-start justify-between gap-3">
                    <div>
                      <CardTitle>{t('reports.features')}</CardTitle>
                      <CardDescription>{t('usage.topRoutesHint')}</CardDescription>
                    </div>
                    {csv('features')}
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <BarList
                      items={d.features.topRoutes.slice(0, 12).map((r) => ({
                        key: r.route,
                        label: <Mono>{r.route}</Mono>,
                        value: r.views,
                        suffix: t('reports.businessesCount', { count: r.businesses }),
                      }))}
                    />
                    <BarList
                      tone="violet"
                      items={d.features.topClicks.slice(0, 12).map((r) => ({
                        key: r.code,
                        label: <Mono>{r.code}</Mono>,
                        value: r.clicks,
                        suffix: t('reports.businessesCount', { count: r.businesses }),
                      }))}
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex-row items-start justify-between gap-3">
                    <div className="flex gap-2">
                      <Sparkles className="mt-0.5 size-4 text-violet" aria-hidden />
                      <div>
                        <CardTitle>{t('reports.ai')}</CardTitle>
                        <CardDescription>
                          {t('reports.aiHint', { budget: shekels(d.defaultBudgetMinor) })}
                        </CardDescription>
                      </div>
                    </div>
                    {csv('ai')}
                  </CardHeader>
                  <CardContent>
                    <BarList
                      tone="orange"
                      empty={t('reports.aiNone')}
                      max={100}
                      format={(v) => `${v}%`}
                      items={d.aiBudget.slice(0, 20).map((r: AiRow) => ({
                        key: `${r.id}-${r.month}`,
                        label: `${r.name} · ${r.month.slice(0, 7)}`,
                        value: Math.round((r.costMinor / Math.max(1, r.budgetMinor)) * 100),
                        suffix: `${shekels(r.costMinor)} / ${shekels(r.budgetMinor)}`,
                      }))}
                    />
                    {d.aiBudget.some((r) => r.costMinor > r.budgetMinor) ? (
                      <p className="mt-3 text-xs text-warning">{t('reports.aiOver')}</p>
                    ) : null}
                  </CardContent>
                </Card>
              </div>
              <p className="text-2xs text-fg-subtle">
                {t('reports.definitions', {
                  active: percent(d.totals.users.active, Math.max(1, d.totals.users.total)),
                })}
              </p>
            </>
          ) : (
            <Skeleton className="h-64 w-full" />
          )}
        </>
      )}

      <BillingReport />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-(--radius-md) border border-border bg-surface-sunken/50 px-3 py-2">
      <dt className="text-2xs text-fg-muted">{label}</dt>
      <dd className="tabular text-sm font-semibold text-fg">{value}</dd>
    </div>
  );
}

function Mono({ children }: { children: string }) {
  return (
    <span dir="ltr" className="font-mono text-2xs">
      {children}
    </span>
  );
}
